/**
 * Wave 95.1 — pending-count digest hook.
 *
 * Polls /v1/agent/digest (already shipped in Wave 52, anomaly counts
 * added in Wave 88) every 60 s and exposes a typed object the nav can
 * use to hang blue dots on the right menu items. Cheap query — single
 * round-trip aggregating across modules.
 */

import { useEffect, useState } from 'react';
import { request } from '@/api/client';
import { useAuth } from '@/store/auth.store';

export interface Digest {
  unreadMessages: number;
  rncsAssigned: number;
  pendingApprovals: number;
  overdueMedicoes: number;
  anomalies?: { block: number; high: number; warn: number; total: number };
  total: number;
}

export function useDigest(): Digest | null {
  const token = useAuth((s) => s.token);
  const [digest, setDigest] = useState<Digest | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      if (cancelled || document.hidden) return;
      try {
        const r = await request<Digest>('/v1/agent/digest');
        if (!cancelled) setDigest(r);
      } catch {
        // ignore — keep last good
      }
    };
    pull();
    const iv = window.setInterval(pull, 60_000);
    const onVis = () => { if (!document.hidden) pull(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token]);

  return digest;
}
